import { graphql, Link, useStaticQuery } from 'gatsby';
import React, { useMemo, useState } from 'react';
import { convertResourceCategoryToPageSlug } from 'src/functions/resourceCategoryToSlug';
import ChevronWhite from 'src/app_previous/images/sliders/chevron.white';
import ResourceCenterMobileMenu from 'app_previous/molecules/Article/Menu/Menu.mobile';
import {
  ResourceCenterMenuLinkGroup,
  ResourceCenterMenuLinkSubGroup,
  ResourceCenterMenuContainer,
  ResourceCenterMenuLinks,
  ResourceCenterMenuLinkSubEntry,
  ResourceCenterMenuLinkPost,
  ResourceCenterMenuLinksMobile,
  ChevronWhiteContainer,
} from './Menu.styles';
import { Color } from 'app_previous/styles/colors';

interface ResourceCenterMenuData {
  allContentfulResource: {
    edges: {
      node: {
        subCategory: string;
        category: string;
        slug: string;
        title: string;
      };
    }[];
  };
}

interface ResourceCenterMenuProps {
  dropdownVerticalOrientation?: 'top' | 'bottom';
  backgroundColor?: Color;
  hoverColor?: Color;
}

const ResourceCenterMenu = ({
  dropdownVerticalOrientation = 'bottom',
  backgroundColor = 'chineseBlack',
  hoverColor = 'darkGray',
}: ResourceCenterMenuProps): JSX.Element => {
  const [mobileOpened, setMobileOpened] = useState(false);
  // TODO: Remove this once we have all pro articles categorized
  const { allContentfulResource: data }: ResourceCenterMenuData = useStaticQuery(graphql`
    query ContentfulResourceMenuQuery {
      allContentfulResource(sort: { createdAt: DESC }, filter: { slug: { ne: "attachments-testing" } }) {
        edges {
          node {
            subCategory
            category
            slug
            title
          }
        }
      }
    }
  `);

  const menuCategorizedGroup = useMemo(() => {
    return data.edges.reduce(
      (p, { node }) => {
        if (node.category) {
          if (p[node.category]) {
            const category = p[node.category];
            if (category[node.subCategory]) {
              category[node.subCategory].push({ title: node.title, slug: node.slug });
            } else {
              category[node.subCategory] = [{ title: node.title, slug: node.slug }];
            }
          } else {
            const subCatDict: { [key: string]: { title: string; slug: string }[] } = {};
            subCatDict[node.subCategory] = [{ title: node.title, slug: node.slug }];
            p[node.category] = subCatDict;
          }
        }
        return p;
      },
      {} as { [key: string]: { [key: string]: { title: string; slug: string }[] } }
    );
  }, [data]);

  const toggleMobile = () => {
    setMobileOpened((toggle) => !toggle);
  };

  const throwErrorForNullSubcategory = (posts: { title: string; slug: string }[]) => {
    const slugs = posts.map(({ slug }) => slug);
    throw new Error(
      `The following articles on category 'Resource' don't have a sub category defined: ${slugs.join(', ')}`
    );
  };

  return (
    <>
      <ResourceCenterMenuContainer backgroundColor={backgroundColor}>
        <ResourceCenterMenuLinks>
          {Object.keys(menuCategorizedGroup)
            .sort((a, b) => a.localeCompare(b))
            .map((category, index) => {
              const categoryGroup = menuCategorizedGroup[category];
              const orientation = index / Object.keys(menuCategorizedGroup).length > 0.5 ? 'left' : 'right';
              const categoryGroupArray = Object.keys(categoryGroup).sort((a, b) => a.localeCompare(b));

              return (
                <ResourceCenterMenuLinkGroup
                  key={category}
                  hoverColor={hoverColor}
                >
                  <Link to={`/resources/${convertResourceCategoryToPageSlug(category)}`}>{category}</Link>
                  <ResourceCenterMenuLinkSubGroup
                    horizontalOrientation={orientation}
                    verticalOrientation={dropdownVerticalOrientation}
                    backgroundColor={hoverColor}
                  >
                    {categoryGroupArray.map((subcategory) => {
                      if (subcategory === 'null') {
                        throwErrorForNullSubcategory(categoryGroup[subcategory]);
                      }

                      return (
                        <ResourceCenterMenuLinkSubEntry
                          key={subcategory}
                          horizontalOrientation={orientation}
                        >
                          <Link
                            to={`/resources/${convertResourceCategoryToPageSlug(
                              category
                            )}/${convertResourceCategoryToPageSlug(subcategory)}`}
                          >
                            <ChevronWhiteContainer
                              horizontalOrientation={orientation}
                              className={`arrow-${orientation}`}
                            >
                              <ChevronWhite
                                orientation={orientation}
                                height={14}
                              />
                            </ChevronWhiteContainer>
                            <span>{subcategory}</span>
                          </Link>
                          <ResourceCenterMenuLinkPost
                            horizontalOrientation={orientation}
                            verticalOrientation={dropdownVerticalOrientation}
                            backgroundColor={hoverColor}
                          >
                            {categoryGroup[subcategory]
                              .filter((_post, postIndex) => postIndex < 5)
                              .map((post) => (
                                <ResourceCenterMenuLinkSubEntry key={post.slug}>
                                  <Link to={`/resources/${post.slug}`}>{post.title}</Link>
                                </ResourceCenterMenuLinkSubEntry>
                              ))}
                            {categoryGroup[subcategory].length >= 5 ? (
                              <ResourceCenterMenuLinkSubEntry>
                                <Link
                                  to={`/resources/${convertResourceCategoryToPageSlug(
                                    category
                                  )}/${convertResourceCategoryToPageSlug(subcategory)}`}
                                >
                                  View All
                                </Link>
                              </ResourceCenterMenuLinkSubEntry>
                            ) : null}
                          </ResourceCenterMenuLinkPost>
                        </ResourceCenterMenuLinkSubEntry>
                      );
                    })}
                  </ResourceCenterMenuLinkSubGroup>
                </ResourceCenterMenuLinkGroup>
              );
            })}
        </ResourceCenterMenuLinks>
        <ResourceCenterMenuLinksMobile>
          <ResourceCenterMenuLinkGroup hoverColor={hoverColor}>
            <a onClick={toggleMobile}>View All Articles</a>
          </ResourceCenterMenuLinkGroup>
        </ResourceCenterMenuLinksMobile>
      </ResourceCenterMenuContainer>
      <ResourceCenterMobileMenu
        opened={mobileOpened}
        links={menuCategorizedGroup}
        onOverlayClick={toggleMobile}
      />
    </>
  );
};

export default ResourceCenterMenu;
